import { useQuery } from '@tanstack/react-query';
import { get, isEmpty, set, uniq } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { IComponent, IDaypart } from 'src/interfaces';
import { ISchedule, IScheduleItem } from 'src/interfaces/Schedule';
import { queryKeys, requestCCApi } from 'src/utils';

import { fetchComponents, SCHEDULE_COMPONENT_INCLUDES } from '../components';
import { transformComponent } from '../components/transform-helper';
import { fetchProducts } from '../product';
import { fetchThemes } from '../themes';

export const useGetSchedule = (
  scheduleId: string | number,
  enabledTransform: boolean = false,
) => {
  return useQuery({
    queryKey: queryKeys.schedules.details([
      scheduleId,
      `enabledTransform=${enabledTransform}`,
    ]),
    queryFn: () => fetchSchedule(scheduleId, enabledTransform),
    enabled: Boolean(scheduleId),
  });
};

export const fetchSchedule = async (
  scheduleId: string | number,
  enabledTransform?: boolean,
) => {
  const url = `/schedules/${scheduleId}?include=dayparts`;
  const response = await requestCCApi({ url });

  const scheduleItems: IScheduleItem[] = get(
    response,
    'data.scheduleItems',
    [],
  );

  /* Map daypart */
  const dayparts = get(response, 'includes.dayparts.data', []) as IDaypart[];
  if (response.data) {
    set(
      response,
      'data.daypart',
      dayparts.find((daypart) => daypart.id === response.data.daypart_id),
    );
  }

  /* Map component */
  if (!isEmpty(scheduleItems)) {
    const componentIDs = uniq(scheduleItems.map((item) => item.component_id));

    const backupComponentIDs = uniq(
      scheduleItems
        .flatMap((item) => (Array.isArray(item.backups) ? item.backups : []))
        .map((backup) => backup?.component_id),
    );

    const allComponentIDs = uniq([...componentIDs, ...backupComponentIDs]);
    const componentKeys = `id.list=${allComponentIDs.join('|')}`;
    const components = await queryClient.fetchQuery({
      queryKey: queryKeys.components.all(componentKeys),
      queryFn: () =>
        fetchComponents(
          [componentKeys, 'pageSize=0'],
          false,
          SCHEDULE_COMPONENT_INCLUDES,
        ),
    });

    let componentList = components;

    if (enabledTransform) {
      const [themes, products] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: queryKeys.themes.all(),
          queryFn: () => fetchThemes(),
        }),
        queryClient.fetchQuery({
          queryKey: queryKeys.products.all(),
          queryFn: () => fetchProducts(),
        }),
      ]);

      componentList = components.map((comp) =>
        transformComponent(comp, products, themes),
      );
    }

    const componentMap = new Map<number, IComponent>(
      componentList.map((comp) => [comp.id, comp]),
    );

    response.data.scheduleItems = scheduleItems.map((item) => ({
      ...item,
      component: componentMap.get(item.component_id),
      backups: Array.isArray(item.backups)
        ? item.backups.map((backup: any) => ({
            ...backup,
            component: componentMap.get(backup.component_id),
          }))
        : item.backups,
    }));
  }

  return response.data as ISchedule;
};
